import { RefObject, useEffect } from "react";

type UseClickOutsideOptions = {
  enabled: boolean;
  onClickOutside: () => void;
  excludeRefs?: RefObject<HTMLElement | null>[];
};

/**
 * Hook that triggers a callback when clicking outside of specified elements.
 * Useful for closing popovers, modals, dropdowns, etc.
 * Uses pointerdown event for better mobile/touch support without blocking scrolling.
 *
 * @param enabled - Whether the click outside detection is active
 * @param onClickOutside - Callback to execute when clicking outside
 * @param excludeRefs - Array of refs to exclude from "outside" detection (e.g., trigger buttons, popover content)
 */
export const useClickOutside = ({
  enabled,
  onClickOutside,
  excludeRefs = [],
}: UseClickOutsideOptions): void => {
  useEffect(() => {
    if (!enabled) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;

      for (const ref of excludeRefs) {
        if (ref.current?.contains(target)) return;
      }

      onClickOutside();
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [enabled, onClickOutside, excludeRefs]);
};
